import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  getCoachTeams,
  coachAwardPoints,
  coachMarkPayment,
  getCoachCashSummary,
  getProducts,
  assignProductToAthlete,
  type ClubAthlete,
  type AthleteProduct,
  type PaymentMethod,
  type CoachCashSummary,
  type CoachTeam,
  type Product,
} from "../../../services/api";
import api from "../../../services/api";
import { createInvitation, getSentInvitations, cancelInvitation, type Invitation } from "../../../services/invitations";
import TeamInviteLink from "../../../components/app/TeamInviteLink";
import CoachJoinRequests from "../../../components/app/CoachJoinRequests";
import { useAuth } from "../../../context/AuthContext";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import AgeGroupSelect from "../../../components/AgeGroupSelect";
import { GameButton, GameCard, GameHero, GameIcon } from "../../../components/game";
import { formatDate } from "../../../lib/dateFormat";
import DateInput from "../../../components/DateInput";
import { matchesQuery } from "../../../lib/search";
import { suggestAgeGroup } from "../../../lib/athleteAge";

// Client-side email check that returns a specific, friendly reason (or null)
// so the coach is told *why* an address is wrong (space, extra dot, typo)
// before submitting — mirrors the server-side guard in routes/invitations.ts.
function validateInviteEmail(raw: string): string | null {
  const email = raw.trim();
  if (!email) return "Adresa de email este obligatorie.";
  if (raw !== email) return "Adresa are spații la început sau la sfârșit.";
  if (/\s/.test(email)) return "Adresa conține un spațiu.";
  if (!email.includes("@")) return "Adresei îi lipsește simbolul @.";
  if ((email.match(/@/g) || []).length > 1) return "Adresa conține mai multe simboluri @.";
  if (email.includes("..")) return "Adresa conține două puncte consecutive.";
  const [local, domain] = email.split("@");
  if (!local) return "Lipsește partea dinainte de @.";
  if (!domain) return "Lipsește domeniul după @.";
  if (local.startsWith(".") || local.endsWith(".")) return "Punct în plus lângă @.";
  if (domain.startsWith(".") || domain.endsWith(".")) return "Punct în plus în domeniu.";
  if (!domain.includes(".")) return "Domeniul pare incomplet (ex: @gmail.com).";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Adresa de email nu pare validă.";
  return null;
}

export default function AppCoachAthletes() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clubs: ctxClubs, activeClubId: ctxClubId, user } = useAuth();

  const [athletes, setAthletes] = useState<ClubAthlete[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string; age_group?: string | null; age_group_id?: string | null; age_group_ref?: { id: string; name: string; min_age: number | null; max_age: number | null } | null; min_age?: number | null }[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(searchParams.get("action") === "invite");
  const [showLinkSheet, setShowLinkSheet] = useState(false);
  const [linkTeamId, setLinkTeamId] = useState("");
  const [showAddForm, setShowAddForm] = useState(searchParams.get("action") === "add");
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteTeamId, setInviteTeamId] = useState(searchParams.get("team") || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTeam, setFilterTeam] = useState(searchParams.get("team") || "");
  const [tab, setTab] = useState<"athletes" | "invitations">("athletes");
  // Search input scoped to the Invitations tab — kept separate from
  // `searchQuery` (which filters the Athletes tab) so switching tabs
  // doesn't carry over an unrelated filter.
  const [invitationSearch, setInvitationSearch] = useState("");

  // Athlete detail modal state
  const [selectedAthlete, setSelectedAthlete] = useState<ClubAthlete | null>(null);
  const [athleteProducts, setAthleteProducts] = useState<AthleteProduct[]>([]);
  // Award-points modal
  const [awardOpen, setAwardOpen] = useState(false);
  const [awardPointsValue, setAwardPointsValue] = useState("10");
  const [awardReason, setAwardReason] = useState("");
  const [awardSaving, setAwardSaving] = useState(false);
  const [awardError, setAwardError] = useState("");

  const handleAwardPoints = async () => {
    if (!selectedAthlete) return;
    const pts = parseInt(awardPointsValue, 10);
    if (!Number.isFinite(pts) || pts <= 0) { setAwardError(t("coach.awardInvalidPoints", "Introdu un număr de puncte valid.")); return; }
    if (!awardReason.trim()) { setAwardError(t("coach.awardReasonRequired", "Motivul este obligatoriu.")); return; }
    setAwardSaving(true);
    setAwardError("");
    try {
      const { total_points } = await coachAwardPoints(selectedAthlete.user.id, pts, awardReason.trim());
      setSelectedAthlete((prev) => prev ? { ...prev, stats: { ...prev.stats, points: total_points } } as ClubAthlete : prev);
      setAwardOpen(false);
      setAwardReason("");
      setAwardPointsValue("10");
    } catch (err: any) {
      setAwardError(err?.response?.data?.error || t("coach.awardFailed", "Nu am putut acorda punctele."));
    } finally {
      setAwardSaving(false);
    }
  };
  const [loadingProducts, setLoadingProducts] = useState(false);
  // Add-service picker state — populated on demand when the coach
  // opens the modal, then filtered against the athlete's existing
  // assignments so we don't re-offer something they already have.
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [clubProducts, setClubProducts] = useState<Product[]>([]);
  const [assigningService, setAssigningService] = useState<string | null>(null);

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState<{
    athleteProductId: string;
    athleteName: string;
    productName: string;
    amount: number;
    currency: string;
  } | null>(null);
  const [paymentForm, setPaymentForm] = useState<{
    method: PaymentMethod;
    bank_reference: string;
    notes: string;
  }>({ method: "cash", bank_reference: "", notes: "" });
  const [savingPayment, setSavingPayment] = useState(false);

  // Coach cash summary state
  const [cashSummary, setCashSummary] = useState<CoachCashSummary | null>(null);

  // Manual add form state
  const [addForm, setAddForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    team_id: "",
    birth_date: "",
    age_group: [] as string[],
    has_guardian: false,
    guardian_name: "",
    guardian_email: "",
    guardian_type: "" as "" | "mama" | "tata" | "tutore",
  });

  // Calculate age from birth date
  const calculatedAge = useMemo(() => {
    if (!addForm.birth_date) return null;
    const birth = new Date(addForm.birth_date);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  }, [addForm.birth_date]);

  // Auto-detect age group from birth date. Shared with the server's own
  // mapping so a suggestion here is never a group the server would not pick.
  const suggestedAgeGroup = useMemo(() => suggestAgeGroup(calculatedAge), [calculatedAge]);

  // Resolve selected team's effective min age — prefer numeric `min_age`,
  // fall back to parsing `age_group` labels like "U-12".
  const selectedTeamForAdd = useMemo(
    () => teams.find(team => team.id === addForm.team_id) || null,
    [teams, addForm.team_id]
  );
  // Resolve the team's effective upper-age bound for the guardian gate:
  // prefer the structured age_group_ref.max_age, then numeric min_age
  // override, then parse the legacy "U-XX" label as last resort.
  const teamMinAgeForGate = useMemo(() => {
    if (!selectedTeamForAdd) return null;
    if (selectedTeamForAdd.age_group_ref?.max_age != null) return selectedTeamForAdd.age_group_ref.max_age;
    if (selectedTeamForAdd.min_age != null) return selectedTeamForAdd.min_age;
    const m = selectedTeamForAdd.age_group?.match(/(\d+)/);
    return m ? Number(m[1]) : null;
  }, [selectedTeamForAdd]);

  // Guardian is REQUIRED if the athlete (by birth date) OR the team's
  // effective upper age bound is under 14.
  const guardianRequired =
    (calculatedAge !== null && calculatedAge < 14) ||
    (teamMinAgeForGate !== null && teamMinAgeForGate < 14);

  // Show guardian fields if explicitly checked OR if required by age/team gate
  const showGuardianFields = addForm.has_guardian || guardianRequired;

  const activeClubId = ctxClubId || ctxClubs[0]?.id;

  useEffect(() => {
    const loadData = async () => {
      if (!activeClubId || !user?.id) return;
      setLoading(true);
      try {
        const [coachTeamsData, invitationsData, cashData] = await Promise.all([
          getCoachTeams(user.id),
          getSentInvitations(),
          getCoachCashSummary().catch(() => null), // Fail silently if not a coach
        ]);

        // Extract teams list — carry age_group + age_group_ref + min_age so the
        // guardian-required gate can prefer the structured catalogue's max_age.
        const teamsList = coachTeamsData.map((team: CoachTeam) => ({
          id: team.id,
          name: team.name,
          age_group: team.age_group,
          age_group_id: team.age_group_id,
          age_group_ref: team.age_group_ref,
          min_age: team.min_age,
        }));
        setTeams(teamsList);

        // Extract athletes from coach's teams. An athlete on several of the
        // coach's teams appears once per team in the payload, so collapse to ONE
        // row per athlete carrying ALL their teams (avoids duplicate rows and
        // duplicate React keys).
        const byAthlete = new Map<string, ClubAthlete>();
        for (const team of coachTeamsData as CoachTeam[]) {
          for (const athlete of team.athletes) {
            const existing = byAthlete.get(athlete.id);
            if (existing) {
              const teams = ((existing as any).teams as { id: string; name: string }[]) || [];
              if (!teams.some((t) => t.id === team.id)) teams.push({ id: team.id, name: team.name });
              (existing as any).teams = teams;
              continue;
            }
            byAthlete.set(athlete.id, {
              id: athlete.id,
              user: { id: athlete.id, full_name: athlete.full_name, email: athlete.email },
              team: { id: team.id, name: team.name },
              teams: [{ id: team.id, name: team.name }],
              joined_at: athlete.joined_team_at || null,
              // Set by the backend when the athlete's profile no longer
              // belongs to this club — the club removed them, but their
              // account (and their history on this roster) lives on.
              detached: (athlete as any).detached === true,
              // Backend /users/:userId/coach-teams now aggregates real
              // points + attendance per athlete. Map straight through
              // if present, fall back to zeros only for defensive
              // safety on older payloads.
              stats: {
                attendance: {
                  present: (athlete as any).stats?.sessions_present ?? 0,
                  absent: 0,
                  late: 0,
                  excused: 0,
                  total: (athlete as any).stats?.sessions_total ?? 0,
                  rate: (athlete as any).stats?.attendance_rate ?? 0,
                },
                points: (athlete as any).stats?.points_total ?? 0,
              },
            } as ClubAthlete);
          }
        }
        setAthletes(Array.from(byAthlete.values()));

        // Filter invitations to only show athlete invitations for this club
        setInvitations(invitationsData.filter(inv => inv.role_code === "athlete" && inv.club_id === activeClubId));
        if (cashData) setCashSummary(cashData);
      } catch (error) {
        console.error("Failed to load athletes:", error);
      }
      setLoading(false);
    };
    loadData();
  }, [activeClubId, user?.id]);

  // An athlete the club removed keeps their account, so the coach may still
  // reach an old roster row. It is read-only: no points, no metrics, no
  // services.
  const selectedDetached = (selectedAthlete as any)?.detached === true;

  // Filter athletes
  const filteredAthletes = athletes.filter(athlete => {
    const matchesSearch = matchesQuery(
      searchQuery,
      athlete.user?.full_name,
      athlete.user?.email
    );
    const athleteTeams = ((athlete as any).teams as { id: string; name: string }[]) || (athlete.team ? [athlete.team] : []);
    const matchesTeam = !filterTeam || athleteTeams.some((t) => t.id === filterTeam);
    return matchesSearch && matchesTeam;
  });

  // Filter pending invitations (drives the tab badge count)
  const pendingInvitations = invitations.filter(inv => inv.status === "pending");

  // Visible invitations on the Invitations tab — hide already-accepted ones
  // (they migrate to Athletes once the user finishes onboarding, so keeping
  // them here would just be noise) and apply the in-tab search.
  const visibleInvitations = invitations
    .filter(inv => inv.status !== "accepted")
    .filter(inv =>
      matchesQuery(invitationSearch, inv.email)
    );

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailErr = validateInviteEmail(inviteEmail);
    if (emailErr) {
      setError(emailErr);
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload: any = {
        email: inviteEmail.trim(),
        role_code: "athlete" as const,
        club_id: activeClubId,
      };
      if (inviteTeamId) {
        payload.team_id = inviteTeamId;
      }

      const newInvitation = await createInvitation(payload);
      setInvitations(prev => [...prev, newInvitation]);
      if (newInvitation.email_sent === false) {
        // Invitation saved, but the email did not go out — tell the truth
        // instead of a false "sent" so the coach can act on it.
        setError(t("coach.inviteEmailFailed", "Invitația a fost salvată, dar emailul NU a putut fi trimis. Verifică adresa sau contactează administratorul."));
      } else {
        setSuccess(t("coach.inviteSent", "Invitația a fost trimisă!"));
        setInviteEmail("");
        setInviteTeamId("");
        setTimeout(() => {
          setShowInviteForm(false);
          setSearchParams({});
          setSuccess("");
        }, 2000);
      }
    } catch (err: any) {
      console.error("Failed to send invitation:", err);
      setError(err.response?.data?.error || t("coach.inviteError", "Nu s-a putut trimite invitația"));
    }
    setSaving(false);
  };

  const handleAddAthlete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.full_name) {
      setError(t("coach.nameRequired", "Numele este obligatoriu"));
      return;
    }
    if (!addForm.email) {
      setError(t("coach.emailRequired", "Email-ul este obligatoriu"));
      return;
    }
    if (!addForm.team_id) {
      setError(t("coach.teamRequired", "Echipa este obligatorie"));
      return;
    }

    // Validate guardian fields if required
    if (showGuardianFields) {
      if (!addForm.guardian_name) {
        setError(t("coach.guardianNameRequired", "Numele părintelui/tutorelui este obligatoriu"));
        return;
      }
      if (!addForm.guardian_email) {
        setError(t("coach.guardianEmailRequired", "Email-ul părintelui/tutorelui este obligatoriu"));
        return;
      }
      if (!addForm.guardian_type) {
        setError(t("coach.guardianTypeRequired", "Tipul părintelui/tutorelui este obligatoriu"));
        return;
      }
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload: any = {
        full_name: addForm.full_name,
        email: addForm.email,
        phone: addForm.phone || null,
        birth_date: addForm.birth_date || null,
        age_group: addForm.age_group.length > 0 ? addForm.age_group[0] : (suggestedAgeGroup || null),
        has_guardian: showGuardianFields,
      };

      if (showGuardianFields) {
        payload.guardian_contact = {
          full_name: addForm.guardian_name,
          email: addForm.guardian_email,
          phone: null,
          relationship: addForm.guardian_type,
        };
      }

      const { data } = await api.post(`/teams/${addForm.team_id}/create-athlete`, payload);

      // Reload athletes list from coach's teams
      const refreshedTeams = await getCoachTeams(user!.id);
      const refreshedAthletes: ClubAthlete[] = refreshedTeams.flatMap((team: CoachTeam) =>
        team.athletes.map(athlete => ({
          id: athlete.id,
          user: {
            id: athlete.id,
            full_name: athlete.full_name,
            email: athlete.email,
          },
          team: { id: team.id, name: team.name },
          joined_at: athlete.joined_team_at || null,
          detached: (athlete as any).detached === true,
          stats: {
            attendance: {
              present: (athlete as any).stats?.sessions_present ?? 0,
              absent: 0,
              late: 0,
              excused: 0,
              total: (athlete as any).stats?.sessions_total ?? 0,
              rate: (athlete as any).stats?.attendance_rate ?? 0,
            },
            points: (athlete as any).stats?.points_total ?? 0,
          },
        }))
      );
      setAthletes(refreshedAthletes);

      // Backend queues an account-setup email to the athlete (and to the
      // parent when we just created a fresh parent account). Tell the
      // coach so they know what landed in the athlete's inbox.
      const parentEmailSent = data?.parent_setup_email_sent === true;
      setSuccess(
        parentEmailSent
          ? t("coach.athleteAddedParentMail", "Sportivul a fost adăugat. Email de activare trimis sportivului și părintelui.")
          : t("coach.athleteAddedMail", "Sportivul a fost adăugat. Email de activare trimis pe {{email}}.", { email: addForm.email })
      );
      setAddForm({
        full_name: "",
        email: "",
        phone: "",
        team_id: "",
        birth_date: "",
        age_group: [],
        has_guardian: false,
        guardian_name: "",
        guardian_email: "",
        guardian_type: "",
      });
      setTimeout(() => {
        setShowAddForm(false);
        setSearchParams({});
        setSuccess("");
      }, 2000);
    } catch (err: any) {
      console.error("Failed to add athlete:", err);
      setError(err.response?.data?.error || t("coach.addError", "Nu s-a putut adăuga sportivul"));
    }
    setSaving(false);
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    } catch (err) {
      console.error("Failed to cancel invitation:", err);
    }
  };

  // Open athlete detail modal and fetch their products
  const handleSelectAthlete = async (athlete: ClubAthlete) => {
    setSelectedAthlete(athlete);
    setLoadingProducts(true);
    try {
      const { data } = await api.get<AthleteProduct[]>(`/products/athlete/${athlete.user.id}`);
      setAthleteProducts(data);
    } catch (err) {
      console.error("Failed to fetch athlete products:", err);
      setAthleteProducts([]);
    }
    setLoadingProducts(false);
  };

  // Open the add-service picker. Fetches the club catalog on demand
  // so we don't ship products the coach never opens the modal for.
  const handleOpenAddService = async () => {
    if (!activeClubId) return;
    setShowAddServiceModal(true);
    try {
      const products = await getProducts(activeClubId);
      setClubProducts(products.filter((p) => p.is_active !== false));
    } catch (err) {
      console.error("Failed to load club products:", err);
      setClubProducts([]);
    }
  };

  // Assign a service to the selected athlete. Refreshes the modal's
  // athlete-products list so the new service appears immediately.
  const handleAssignService = async (productId: string) => {
    if (!selectedAthlete) return;
    setAssigningService(productId);
    try {
      await assignProductToAthlete(selectedAthlete.user.id, { product_id: productId });
      const { data } = await api.get<AthleteProduct[]>(
        `/products/athlete/${selectedAthlete.user.id}`
      );
      setAthleteProducts(data);
      setShowAddServiceModal(false);
      setSuccess(t("coach.serviceAdded", "Serviciul a fost atribuit"));
    } catch (err: any) {
      setError(err?.response?.data?.error || "Nu s-a putut atribui serviciul");
    } finally {
      setAssigningService(null);
    }
  };

  // Open payment modal
  const handleOpenPaymentModal = (
    athleteProductId: string,
    athleteName: string,
    productName: string,
    amount: number,
    currency: string
  ) => {
    setPaymentModal({ athleteProductId, athleteName, productName, amount, currency });
    setPaymentForm({ method: "cash", bank_reference: "", notes: "" });
  };

  // Submit payment
  const handleSubmitPayment = async () => {
    if (!paymentModal) return;
    setSavingPayment(true);
    try {
      await coachMarkPayment(paymentModal.athleteProductId, {
        payment_method: paymentForm.method,
        bank_reference: paymentForm.bank_reference || undefined,
        notes: paymentForm.notes || undefined,
      });
      // Refresh products for this athlete
      if (selectedAthlete) {
        const { data } = await api.get<AthleteProduct[]>(`/products/athlete/${selectedAthlete.user.id}`);
        setAthleteProducts(data);
      }
      setPaymentModal(null);
      setSuccess(t("coach.paymentMarked", "Plata a fost marcată cu succes"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      console.error("Failed to mark payment:", err);
      setError(err.response?.data?.error || t("coach.paymentError", "Nu s-a putut marca plata"));
      setTimeout(() => setError(""), 3000);
    }
    setSavingPayment(false);
  };

  // Format currency
  const formatCurrency = (cents: number, currency: string = "RON") => {
    return new Intl.NumberFormat("ro-RO", { style: "currency", currency }).format(cents / 100);
  };

  const getAttendanceColor = (rate: number) => {
    if (rate >= 80) return "text-emerald-600 bg-emerald-100";
    if (rate >= 60) return "text-amber-600 bg-amber-100";
    return "text-red-600 bg-red-100";
  };

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Header — Add lives in the hero to reclaim the vertical space the
            standalone Add row used to take. */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-extrabold">{t("coach.athletes", "Sportivi")}</h1>
              <p className="text-white/70 text-[13px] mt-0.5">{athletes.length} {t("coach.athletes", "Sportivi")}</p>
            </div>
            <button
              onClick={() => setShowActionMenu(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white/15 hover:bg-white/25 text-white rounded-xl font-bold text-[13px] transition-colors shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t("coach.add", "Adaugă")}
            </button>
          </div>
        </GameHero>

        {/* Add action — bottom sheet (a dropdown can't escape the hero's
            overflow-hidden, and a sheet is friendlier on mobile). */}
        {showActionMenu && (
          <div
            className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center"
            onClick={() => setShowActionMenu(false)}
          >
            <div
              className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl p-2 sm:mb-0"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* The link is how a team actually gets filled — one message in
                  the parents' group — so it leads. Email, one address at a
                  time, is the fallback underneath it. */}
              <button
                onClick={() => { setShowActionMenu(false); setShowLinkSheet(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-slate-700 hover:bg-slate-50 rounded-2xl transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-3 3a4 4 0 005.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold">{t("coach.inviteByLink", "Trimite linkul echipei")}</p>
                  <p className="text-xs text-slate-500">{t("coach.inviteByLinkDesc", "Un mesaj în grupul părinților")}</p>
                </div>
              </button>
              <button
                onClick={() => { setShowActionMenu(false); setShowInviteForm(true); setSearchParams({ action: "invite" }); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-slate-700 hover:bg-slate-50 rounded-2xl transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold">{t("coach.inviteByEmail", "Invită prin email")}</p>
                  <p className="text-xs text-slate-500">{t("coach.inviteByEmailDesc", "Câte o adresă pe rând")}</p>
                </div>
              </button>
              <button
                onClick={() => { setShowActionMenu(false); setShowAddForm(true); setSearchParams({ action: "add" }); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-slate-700 hover:bg-slate-50 rounded-2xl transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold">{t("coach.addManually", "Adaugă manual")}</p>
                  <p className="text-xs text-slate-500">{t("coach.addManuallyDesc", "Creează cont direct")}</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* The team link, in a sheet: pick the team, then share. */}
        {showLinkSheet && (
          <div
            className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center"
            onClick={() => setShowLinkSheet(false)}
          >
            <div
              className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl p-5"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-extrabold text-slate-800">
                  {t("coach.inviteByLink", "Trimite linkul echipei")}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowLinkSheet(false)}
                  className="p-2 -mr-2 text-2xl leading-none text-slate-400"
                  aria-label={t("common.close", "Închide")}
                >
                  ×
                </button>
              </div>

              {teams.length === 0 ? (
                <p className="text-sm text-slate-500">
                  {t("coach.noTeamsForLink", "Nu ai încă nicio echipă.")}
                </p>
              ) : (
                <>
                  <label className="block mb-1 text-xs font-bold text-slate-600" htmlFor="link-team">
                    {t("coach.team", "Echipa")}
                  </label>
                  <select
                    id="link-team"
                    value={linkTeamId || teams[0].id}
                    onChange={(e) => setLinkTeamId(e.target.value)}
                    className="w-full px-4 py-3 mb-4 text-sm bg-white border rounded-2xl border-slate-200 text-slate-800"
                  >
                    {teams.map((tm) => (
                      <option key={tm.id} value={tm.id}>
                        {tm.name}
                      </option>
                    ))}
                  </select>
                  <TeamInviteLink
                    teamId={linkTeamId || teams[0].id}
                    teamName={teams.find((tm) => tm.id === (linkTeamId || teams[0].id))?.name}
                    compact
                  />
                </>
              )}
            </div>
          </div>
        )}

        <div className="px-4 pt-5 space-y-4">
        {/* Children waiting for this coach's decision, before anything else on
            the screen: they cannot train until someone says yes. */}
        <CoachJoinRequests clubId={activeClubId} />

        {/* The team link, on the page rather than only inside the Adaugă menu.
            This is how a team actually gets filled — one message in the
            parents' group — and it was three taps deep behind a "+" and a
            bottom sheet, so coaches reported it as missing. With one team the
            share controls are right here; with several, the sheet asks which. */}
        {teams.length > 0 && (
          <GameCard className="p-4">
            {teams.length === 1 ? (
              <TeamInviteLink teamId={teams[0].id} teamName={teams[0].name} compact />
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 shrink-0 rounded-xl">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-3 3a4 4 0 005.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">
                    {t("coach.inviteByLink", "Trimite linkul echipei")}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t("coach.inviteByLinkDesc", "Un mesaj în grupul părinților")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLinkSheet(true)}
                  className="px-4 py-2 text-xs font-extrabold text-white bg-indigo-600 shrink-0 rounded-xl"
                >
                  {t("coach.chooseTeam", "Alege echipa")}
                </button>
              </div>
            )}
          </GameCard>
        )}

        {/* Coach Cash Summary Card */}
        {cashSummary && cashSummary.pending_cash_cents > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 shadow-[0_3px_0_#fde9c8]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-amber-700 font-medium">{t("coach.cashCollected", "Numerar încasat luna asta")}</p>
                  <p className="text-xl font-bold text-amber-800">{formatCurrency(cashSummary.pending_cash_cents, "RON")}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-amber-600">{cashSummary.pending_payments.length} {t("coach.payments", "plăți")}</p>
                <p className="text-xs text-amber-500">{t("coach.pendingHandover", "de predat")}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
          <button
            onClick={() => setTab("athletes")}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              tab === "athletes"
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("coach.athletes", "Sportivi")} ({filteredAthletes.length})
          </button>
          <button
            onClick={() => setTab("invitations")}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors relative ${
              tab === "invitations"
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("coach.invitations", "Invitații")}
            {pendingInvitations.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                {pendingInvitations.length}
              </span>
            )}
          </button>
        </div>

        {tab === "athletes" && (
          <>
            {/* Search and Filter */}
            <div className="space-y-3">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={t("coach.searchAthletes", "Caută sportivi...")}
                  className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {teams.length > 1 && (
                <select
                  value={filterTeam}
                  onChange={e => setFilterTeam(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">{t("coach.allTeams", "Toate echipele")}</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Athletes List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredAthletes.length === 0 ? (
              <GameCard className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <p className="text-slate-500">{t("coach.noAthletes", "Niciun sportiv găsit")}</p>
                <div className="flex gap-2 justify-center mt-4">
                  <GameButton
                    variant="primary"
                    onClick={() => setShowInviteForm(true)}
                    className="!w-auto !py-2 !px-4 !text-sm !rounded-xl"
                  >
                    {t("coach.inviteFirst", "Invită")}
                  </GameButton>
                  <GameButton
                    variant="mint"
                    onClick={() => setShowAddForm(true)}
                    className="!w-auto !py-2 !px-4 !text-sm !rounded-xl"
                  >
                    {t("coach.addManually", "Adaugă manual")}
                  </GameButton>
                </div>
              </GameCard>
            ) : (
              <div className="space-y-3">
                {filteredAthletes.map(athlete => (
                  <div
                    key={athlete.id}
                    onClick={() => handleSelectAthlete(athlete)}
                    className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_3px_0_#eef2f8] active:translate-y-[2px] transition-transform cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
                        {athlete.user?.full_name?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-800 truncate">
                          {athlete.user?.full_name || t("coach.unnamed", "Fără nume")}
                        </h3>
                        {/* Not the email: these are children, and a coach on a
                            pitch needs the team, not a way to write to them. */}
                        {(((athlete as any).teams as { id: string; name: string }[]) || (athlete.team ? [athlete.team] : [])).map((tm) => (
                          <span key={tm.id} className="inline-block mt-1 mr-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full">
                            {tm.name}
                          </span>
                        ))}
                        {(athlete as any).detached && (
                          <span className="inline-block mt-1 mr-1 px-2 py-0.5 bg-rose-100 text-rose-700 text-xs font-bold rounded-full">
                            {t("coach.removedFromClub", "Șters din club")}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-lg text-xs font-bold ${getAttendanceColor(athlete.stats?.attendance?.rate || 0)}`}>
                          {Math.round(athlete.stats?.attendance?.rate || 0)}%
                        </span>
                        <p className="text-xs text-slate-400 mt-1">{t("coach.attendance", "prezență")}</p>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                        <span className="text-xs text-slate-500">{athlete.stats?.attendance?.present || 0} prezent</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                        <span className="text-xs text-slate-500">{athlete.stats?.attendance?.late || 0} întârziat</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <span className="text-xs text-slate-500">{athlete.stats?.attendance?.absent || 0} absent</span>
                      </div>
                      <div className="ml-auto flex items-center gap-1">
                        <GameIcon name="star" className="w-4 h-4" />
                        <span className="text-sm font-bold text-slate-700">{athlete.stats?.points || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "invitations" && (
          <div className="space-y-3">
            {/* Search bar — scoped to the Invitations tab. Hidden when there
                are no invitations at all so the empty-state isn't dwarfed. */}
            {invitations.filter(i => i.status !== "accepted").length > 0 && (
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={invitationSearch}
                  onChange={e => setInvitationSearch(e.target.value)}
                  placeholder={t("coach.searchInvitations", "Caută după email...")}
                  className="w-full pl-10 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            )}
            {visibleInvitations.length === 0 ? (
              <GameCard className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-slate-500">
                  {invitationSearch
                    ? t("coach.noMatchingInvitations", "Nicio invitație care să corespundă căutării")
                    : t("coach.noInvitations", "Nicio invitație trimisă")}
                </p>
              </GameCard>
            ) : (
              visibleInvitations.map(invitation => (
                <div
                  key={invitation.id}
                  className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_3px_0_#eef2f8]"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      invitation.status === "pending" ? "bg-amber-100" :
                      invitation.status === "accepted" ? "bg-emerald-100" :
                      "bg-slate-100"
                    }`}>
                      <svg className={`w-5 h-5 ${
                        invitation.status === "pending" ? "text-amber-600" :
                        invitation.status === "accepted" ? "text-emerald-600" :
                        "text-slate-400"
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{invitation.email}</p>
                      <p className="text-xs text-slate-500">
                        {invitation.team?.name || t("coach.noTeam", "Fără echipă")}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        invitation.status === "pending" ? "bg-amber-100 text-amber-700" :
                        invitation.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                        invitation.status === "expired" ? "bg-slate-100 text-slate-600" :
                        "bg-red-100 text-red-700"
                      }`}>
                        {invitation.status === "pending" && t("coach.pending", "În așteptare")}
                        {invitation.status === "accepted" && t("coach.accepted", "Acceptată")}
                        {invitation.status === "expired" && t("coach.expired", "Expirată")}
                        {invitation.status === "cancelled" && t("coach.cancelled", "Anulată")}
                      </span>
                      {invitation.status === "pending" && (
                        <button
                          onClick={() => handleCancelInvitation(invitation.id)}
                          className="block text-xs text-red-500 mt-1 hover:text-red-600"
                        >
                          {t("coach.cancel", "Anulează")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Invite Form Modal */}
        {showInviteForm && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:pb-4 !mt-0">
            <div
              className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col"
              style={{ maxHeight: "100%" }}
            >
              {/* Fixed Header */}
              <div className="flex-shrink-0 px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800">{t("coach.inviteAthlete", "Invită sportiv")}</h2>
                <button
                  onClick={() => {
                    setShowInviteForm(false);
                    setSearchParams({});
                    setInviteEmail("");
                    setInviteTeamId("");
                    setError("");
                    setSuccess("");
                  }}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-5">
                <form id="invite-form" onSubmit={handleInvite} className="space-y-5">
                  {error && (
                    <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm flex items-center gap-2">
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl text-sm flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {success}
                    </div>
                  )}

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.email", "Email")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      className={`w-full px-4 py-3 bg-slate-50 border rounded-xl text-slate-800 focus:outline-none focus:ring-2 ${
                        inviteEmail && validateInviteEmail(inviteEmail)
                          ? "border-red-300 focus:ring-red-400 focus:border-red-400"
                          : "border-slate-200 focus:ring-indigo-500 focus:border-indigo-500"
                      }`}
                      placeholder="sportiv@email.com"
                      required
                    />
                    {inviteEmail && validateInviteEmail(inviteEmail) && (
                      <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /></svg>
                        {validateInviteEmail(inviteEmail)}
                      </p>
                    )}
                  </div>

                  {/* Team — required by the backend, so the dropdown reflects
                      that. If the coach has no teams yet, we surface a direct
                      shortcut to the team-creation screen instead of leaving
                      them stuck in front of an empty <select>. */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.team", "Echipa")} <span className="text-red-500">*</span>
                    </label>
                    {teams.length === 0 ? (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-sm text-amber-800 font-medium">
                          {t("coach.noTeamsYetTitle", "Nu ai încă nicio echipă")}
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          {t("coach.noTeamsYetBody", "Creează o echipă întâi, apoi poți invita sportivi în ea.")}
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate("/app/coach/teams")}
                          className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {t("coach.goCreateTeam", "Crează o echipă")}
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <select
                          value={inviteTeamId}
                          onChange={e => setInviteTeamId(e.target.value)}
                          required
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none cursor-pointer"
                        >
                          <option value="">{t("coach.selectTeam", "Selectează echipa")}</option>
                          {teams.map(team => (
                            <option key={team.id} value={team.id}>{team.name}</option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-blue-700">
                        <p className="font-medium">{t("coach.inviteInfo", "Cum funcționează?")}</p>
                        <p className="mt-1 text-blue-600">
                          {t("coach.inviteInfoText", "Sportivul va primi un email cu un link de înregistrare. După ce își creează contul, va fi automat adăugat în club.")}
                        </p>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Fixed Footer */}
              <div className="flex-shrink-0 px-5 py-4 border-t border-slate-100 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteForm(false);
                    setSearchParams({});
                    setInviteEmail("");
                    setInviteTeamId("");
                    setError("");
                    setSuccess("");
                  }}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <GameButton
                  variant="primary"
                  type="submit"
                  form="invite-form"
                  disabled={saving || !!validateInviteEmail(inviteEmail)}
                  className="flex-1"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t("common.sending", "Se trimite...")}
                    </>
                  ) : (
                    t("coach.sendInvite", "Trimite invitația")
                  )}
                </GameButton>
              </div>
            </div>
          </div>
        )}

        {/* Manual Add Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:pb-4 !mt-0">
            <div
              className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col"
              style={{ maxHeight: "100%" }}
            >
              {/* Fixed Header */}
              <div className="flex-shrink-0 px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800">{t("coach.addAthlete", "Adaugă sportiv")}</h2>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setSearchParams({});
                    setAddForm({ full_name: "", email: "", phone: "", team_id: "", birth_date: "", age_group: [], has_guardian: false, guardian_name: "", guardian_email: "", guardian_type: "" });
                    setError("");
                    setSuccess("");
                  }}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-5">
                <form id="add-athlete-form" onSubmit={handleAddAthlete} className="space-y-5">
                  {error && (
                    <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm flex items-center gap-2">
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl text-sm flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {success}
                    </div>
                  )}

                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.fullName", "Nume complet")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={addForm.full_name}
                      onChange={e => setAddForm({ ...addForm, full_name: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder={t("coach.fullNamePlaceholder", "ex: Ion Popescu")}
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.email", "Email")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={addForm.email}
                      onChange={e => setAddForm({ ...addForm, email: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="sportiv@email.com"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.phone", "Telefon")}
                    </label>
                    <input
                      type="tel"
                      value={addForm.phone}
                      onChange={e => setAddForm({ ...addForm, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder={t("coach.phonePlaceholder", "ex: 0722 123 456")}
                    />
                  </div>

                  {/* Birth Date */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.birthDate", "Data nașterii")}
                    </label>
                    <DateInput
                      value={addForm.birth_date}
                      onChange={(v) => {
                        const newBirthDate = v;
                        setAddForm({ ...addForm, birth_date: newBirthDate });
                      }}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      max={new Date().toISOString().split('T')[0]}
                    />
                    {calculatedAge !== null && (
                      <p className="text-xs text-slate-500 mt-1">
                        {t("coach.calculatedAge", "Vârstă calculată")}: {calculatedAge} {t("coach.years", "ani")}
                        {suggestedAgeGroup && ` (${t("coach.suggestedGroup", "grupă sugerată")}: ${suggestedAgeGroup})`}
                      </p>
                    )}
                  </div>

                  {/* Age Group */}
                  <div>
                    <AgeGroupSelect
                      value={addForm.age_group}
                      onChange={(vals) => setAddForm({ ...addForm, age_group: vals })}
                      label={t("coach.ageGroup", "Grupă de vârstă")}
                      placeholder={suggestedAgeGroup
                        ? `${t("coach.suggestedAgeGroup", "Sugestie")}: ${suggestedAgeGroup}`
                        : t("coach.selectAgeGroup", "Selectează grupa de vârstă")
                      }
                      multiple={false}
                    />
                  </div>

                  {/* Guardian Checkbox */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="has_guardian"
                      checked={addForm.has_guardian || guardianRequired}
                      disabled={guardianRequired}
                      onChange={e => setAddForm({ ...addForm, has_guardian: e.target.checked })}
                      className="mt-1 w-5 h-5 text-emerald-600 bg-slate-50 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <div>
                      <label htmlFor="has_guardian" className={`text-sm font-medium ${guardianRequired ? 'text-amber-700' : 'text-slate-700'}`}>
                        {t("coach.hasGuardian", "Sportivul este reprezentat de un părinte/tutore")}
                      </label>
                      {guardianRequired && (
                        <p className="text-xs text-amber-600 mt-0.5">
                          {t("coach.guardianRequiredNote", "Obligatoriu pentru sportivi sub 14 ani")}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Guardian Fields */}
                  {showGuardianFields && (
                    <div className="space-y-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <h4 className="text-sm font-medium text-amber-800 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t("coach.guardianInfo", "Informații părinte/tutore")}
                      </h4>

                      {/* Guardian Name */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t("coach.guardianName", "Nume părinte/tutore")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={addForm.guardian_name}
                          onChange={e => setAddForm({ ...addForm, guardian_name: e.target.value })}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder={t("coach.guardianNamePlaceholder", "ex: Maria Popescu")}
                        />
                      </div>

                      {/* Guardian Email */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t("coach.guardianEmail", "Email părinte/tutore")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={addForm.guardian_email}
                          onChange={e => setAddForm({ ...addForm, guardian_email: e.target.value })}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="parinte@email.com"
                        />
                      </div>

                      {/* Guardian Type */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t("coach.guardianType", "Tip reprezentant")} <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-3">
                          {[
                            { value: "mama", label: t("coach.mother", "Mamă") },
                            { value: "tata", label: t("coach.father", "Tată") },
                            { value: "tutore", label: t("coach.guardian", "Tutore") },
                          ].map(option => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setAddForm({ ...addForm, guardian_type: option.value as "mama" | "tata" | "tutore" })}
                              className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
                                addForm.guardian_type === option.value
                                  ? "bg-amber-500 text-white shadow-sm"
                                  : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Team */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.team", "Echipa")} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={addForm.team_id}
                        onChange={e => setAddForm({ ...addForm, team_id: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none cursor-pointer"
                        required
                      >
                        <option value="">{t("coach.selectTeamRequired", "Selectează echipa")}</option>
                        {teams.map(team => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-emerald-700">
                        <p className="font-medium">{t("coach.addInfo", "Ce se întâmplă?")}</p>
                        <p className="mt-1 text-emerald-600">
                          {t("coach.addInfoText", "Se va crea un cont pentru sportiv cu o parolă generată automat. Sportivul își poate schimba parola ulterior.")}
                        </p>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Fixed Footer */}
              <div className="flex-shrink-0 px-5 py-4 border-t border-slate-100 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setSearchParams({});
                    setAddForm({ full_name: "", email: "", phone: "", team_id: "", birth_date: "", age_group: [], has_guardian: false, guardian_name: "", guardian_email: "", guardian_type: "" });
                    setError("");
                    setSuccess("");
                  }}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <GameButton
                  variant="mint"
                  type="submit"
                  form="add-athlete-form"
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t("common.saving", "Se salvează...")}
                    </>
                  ) : (
                    t("coach.addAthlete", "Adaugă sportiv")
                  )}
                </GameButton>
              </div>
            </div>
          </div>
        )}

        {/* Athlete Detail Modal */}
        {selectedAthlete && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:pb-4 !mt-0">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl max-h-full overflow-hidden flex flex-col">
              {/* Header */}
              {/* min-w-0 + truncate: a long email used to push the close
                  button off the right edge of the phone screen. */}
              <div className="flex-shrink-0 px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold">
                    {selectedAthlete.user?.full_name?.charAt(0) || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-bold text-slate-800 truncate">{selectedAthlete.user?.full_name}</h2>
                    {selectedDetached && (
                      <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold">
                        {t("coach.removedFromClub", "Șters din club")}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedAthlete(null);
                    setAthleteProducts([]);
                  }}
                  className="p-2 shrink-0 hover:bg-slate-100 rounded-full"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {selectedDetached && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                    {t(
                      "coach.removedFromClubHint",
                      "Sportivul a fost șters din club. Contul lui rămâne activ, dar nu îi mai poți acorda puncte, modifica metrici sau atribui servicii."
                    )}
                  </div>
                )}

                {/* Stats Section — compact tiles with clear, colored icons. */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-50 rounded-2xl p-3 text-center">
                    <div className="w-8 h-8 mx-auto rounded-xl bg-emerald-100 grid place-items-center mb-1.5">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-lg font-bold text-slate-800 tabular-nums leading-none">{Math.round(selectedAthlete.stats?.attendance?.rate || 0)}%</p>
                    <p className="text-[11px] text-slate-500 mt-1">{t("coach.attendance", "Prezență")}</p>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-3 text-center">
                    <div className="w-8 h-8 mx-auto rounded-xl bg-amber-100 grid place-items-center mb-1.5">
                      <GameIcon name="star" className="w-4 h-4" />
                    </div>
                    <p className="text-lg font-bold text-slate-800 tabular-nums leading-none">{selectedAthlete.stats?.points || 0}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{t("coach.points", "Puncte")}</p>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-3 text-center">
                    <div className="w-8 h-8 mx-auto rounded-xl bg-indigo-100 grid place-items-center mb-1.5">
                      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-lg font-bold text-slate-800 tabular-nums leading-none">{selectedAthlete.stats?.attendance?.total || 0}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{t("coach.sessions", "Ședințe")}</p>
                  </div>
                </div>

                {/* Quick Actions — compact so labels never wrap. */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      navigate(`/app/coach/athletes/${selectedAthlete.user.id}/metrics`);
                      setSelectedAthlete(null);
                      setAthleteProducts([]);
                    }}
                    disabled={selectedDetached}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-violet-500 text-white rounded-xl font-bold text-sm whitespace-nowrap hover:bg-violet-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-violet-500"
                  >
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {t("coach.metrics", "Metrici")}
                  </button>
                  <button
                    onClick={() => { setAwardError(""); setAwardOpen(true); }}
                    disabled={selectedDetached}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-amber-500 text-white rounded-xl font-bold text-sm whitespace-nowrap hover:bg-amber-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-amber-500"
                  >
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    {t("coach.awardPoints", "Acordă puncte")}
                  </button>
                </div>

                {awardOpen && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                    <div className="flex items-end gap-2">
                      <div className="w-24">
                        <label className="block text-xs font-semibold text-slate-600 mb-1">{t("coach.points", "Puncte")}</label>
                        <input
                          type="number"
                          min={1}
                          max={1000}
                          value={awardPointsValue}
                          onChange={(e) => setAwardPointsValue(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-slate-600 mb-1">{t("coach.reason", "Motiv")}</label>
                        <input
                          type="text"
                          value={awardReason}
                          onChange={(e) => setAwardReason(e.target.value)}
                          placeholder={t("coach.awardReasonPlaceholder", "ex: efort deosebit la antrenament")}
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    </div>
                    {awardError && <p className="text-xs text-rose-600">{awardError}</p>}
                    <div className="flex gap-2">
                      <button onClick={() => setAwardOpen(false)} className="flex-1 py-2 text-sm font-semibold rounded-lg bg-white border border-slate-300 text-slate-600">
                        {t("common.cancel", "Anulează")}
                      </button>
                      <button onClick={handleAwardPoints} disabled={awardSaving} className="flex-1 py-2 text-sm font-bold rounded-lg bg-amber-600 text-white disabled:opacity-60">
                        {awardSaving ? t("common.saving", "Se salvează...") : t("coach.awardConfirm", "Acordă")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Services Section — separate from Payments so the
                    coach can see the athlete's service catalog and add
                    new services without wading through payment status. */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700">
                      {t("coach.services", "Servicii")}
                    </h3>
                    <button
                      onClick={handleOpenAddService}
                      disabled={selectedDetached}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-indigo-500"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                      </svg>
                      {t("coach.addService", "Adaugă serviciu")}
                    </button>
                  </div>
                  {loadingProducts ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : athleteProducts.length === 0 ? (
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-500">
                        {t("coach.noServices", "Niciun serviciu atribuit")}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 mb-6">
                      {athleteProducts.map((ap) => (
                        <div key={`svc-${ap.id}`} className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg">
                          <div className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {ap.product?.name}
                            </p>
                            {ap.product?.recurrence_type && (
                              <p className="text-xs text-slate-500">
                                {ap.product.recurrence_type === "monthly"
                                  ? t("coach.monthly", "Lunar")
                                  : ap.product.recurrence_type === "weekly"
                                    ? t("coach.weekly", "Săptămânal")
                                    : t("coach.hourly", "Pe oră")}
                                {ap.product.price_cents > 0 && (
                                  <> · {formatCurrency(ap.product.price_cents, ap.product.currency)}</>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Payments Section */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">{t("coach.payments", "Plăți")}</h3>
                  {loadingProducts ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : athleteProducts.length === 0 ? (
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-500">{t("coach.noProducts", "Nu are servicii atribuite")}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {athleteProducts.map(product => (
                        <div key={product.id} className="bg-slate-50 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-800">{product.product?.name}</p>
                              <p className="text-sm text-slate-500">
                                {formatCurrency(product.product?.price_cents || 0, product.product?.currency)}
                              </p>
                            </div>
                            <div className="text-right">
                              {product.payment_status === "paid" ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  {t("coach.paid", "Plătit")}
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleOpenPaymentModal(
                                    product.id,
                                    selectedAthlete.user?.full_name || "",
                                    product.product?.name || "",
                                    product.product?.price_cents || 0,
                                    product.product?.currency || "RON"
                                  )}
                                  disabled={selectedDetached}
                                  className="px-3 py-1.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-500"
                                >
                                  {t("coach.markPaid", "Marchează plătit")}
                                </button>
                              )}
                            </div>
                          </div>
                          {product.payment_status === "paid" && product.paid_at && (
                            <p className="text-xs text-slate-400 mt-2">
                              {t("coach.paidOn", "Plătit pe")} {formatDate(new Date(product.paid_at))}
                              {product.payment_method && ` • ${product.payment_method === "cash" ? "Numerar" : product.payment_method === "card" ? "Card" : "Transfer"}`}
                            </p>
                          )}
                          {product.payment_status !== "paid" && product.next_due_date && (
                            <p className="text-xs text-amber-600 mt-2">
                              {t("coach.dueDate", "Scadent")} {formatDate(new Date(product.next_due_date))}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add-service picker — lists the club catalog minus what's
            already assigned. z-[65] so it stacks above the athlete
            modal (z-50-ish) but below the payment confirm modal
            (z-[70]) if by chance both are open. */}
        {showAddServiceModal && selectedAthlete && (
          <div className="fixed inset-0 bg-black/50 z-[65] flex items-end sm:items-center justify-center p-4 !mt-0">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-xl max-h-[80vh] flex flex-col">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800">
                  {t("coach.addServiceTitle", "Alege serviciul")}
                </h2>
                <button
                  onClick={() => setShowAddServiceModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-full"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                {(() => {
                  const assignedIds = new Set(athleteProducts.map((ap) => ap.product?.id).filter(Boolean));
                  const available = clubProducts.filter((p) => !assignedIds.has(p.id));
                  if (available.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <p className="text-sm text-slate-500">
                          {t("coach.noServicesAvailable", "Nu există alte servicii disponibile în catalogul clubului.")}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      {available.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => handleAssignService(p.id)}
                          disabled={assigningService !== null}
                          className="w-full text-left p-3 border border-slate-200 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-800 truncate">{p.name}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {p.recurrence_type === "monthly"
                                  ? t("coach.monthly", "Lunar")
                                  : p.recurrence_type === "weekly"
                                    ? t("coach.weekly", "Săptămânal")
                                    : t("coach.hourly", "Pe oră")}
                                {p.price_cents > 0 && (
                                  <> · {formatCurrency(p.price_cents, p.currency)}</>
                                )}
                              </p>
                            </div>
                            {assigningService === p.id ? (
                              <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            ) : (
                              <svg className="w-5 h-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Payment Confirmation Modal */}
        {paymentModal && (
          <div className="fixed inset-0 bg-black/50 z-[70] flex items-end sm:items-center justify-center p-4 !mt-0">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800">{t("coach.confirmPayment", "Confirmă plata")}</h2>
                <button
                  onClick={() => setPaymentModal(null)}
                  className="p-2 hover:bg-slate-100 rounded-full"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500">{t("coach.athlete", "Sportiv")}</p>
                  <p className="font-semibold text-slate-800">{paymentModal.athleteName}</p>
                  <p className="text-sm text-slate-500 mt-2">{t("coach.service", "Serviciu")}</p>
                  <p className="font-semibold text-slate-800">{paymentModal.productName}</p>
                  <p className="text-sm text-slate-500 mt-2">{t("coach.amount", "Suma")}</p>
                  <p className="text-xl font-bold text-emerald-600">{formatCurrency(paymentModal.amount, paymentModal.currency)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("coach.paymentMethod", "Metodă de plată")}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {(["cash", "card", "bank_transfer"] as PaymentMethod[]).map(method => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setPaymentForm(prev => ({ ...prev, method }))}
                        className={`px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                          paymentForm.method === method
                            ? "bg-indigo-500 text-white border-indigo-500"
                            : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300"
                        }`}
                      >
                        {method === "cash" && t("coach.cash", "Numerar")}
                        {method === "card" && t("coach.card", "Card")}
                        {method === "bank_transfer" && t("coach.transfer", "Transfer")}
                      </button>
                    ))}
                  </div>
                </div>

                {paymentForm.method === "cash" && (
                  <div className="p-3 bg-amber-50 rounded-xl text-sm text-amber-700 flex items-start gap-2">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>{t("coach.cashWarning", "Suma va fi adăugată la încasările tale în numerar ce trebuie predate administratorului.")}</span>
                  </div>
                )}

                {paymentForm.method === "bank_transfer" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.bankReference", "Referință bancară")}
                    </label>
                    <input
                      type="text"
                      value={paymentForm.bank_reference}
                      onChange={e => setPaymentForm(prev => ({ ...prev, bank_reference: e.target.value }))}
                      placeholder={t("coach.bankReferencePlaceholder", "Nr. tranzacție sau referință")}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("coach.notes", "Note (opțional)")}
                  </label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={e => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder={t("coach.notesPlaceholder", "Observații suplimentare...")}
                    rows={2}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>

                <GameButton
                  variant="mint"
                  onClick={handleSubmitPayment}
                  disabled={savingPayment}
                >
                  {savingPayment ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t("common.saving", "Se salvează...")}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t("coach.confirmPaymentBtn", "Confirmă plata")}
                    </>
                  )}
                </GameButton>
              </div>
            </div>
          </div>
        )}

        {/* Backdrop for dropdown */}
        {showActionMenu && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowActionMenu(false)}
          />
        )}
        </div>
      </div>
    </MobileAppLayout>
  );
}
